import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, ClickAwayListener, Divider, Typography } from '@material-ui/core';
import { css } from '@emotion/react';
import { colors } from '@/styles';

const ColumnFilterPanel = ({
    open,
    columnId,
    title,
    onClose,
    onApply,
    onClear,
    children,
    width = 380,
    topOffset = 50,
    showClearButton = true,
    showApplyButton = true,
}) => {
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        if (open) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    const handleApply = () => {
        onApply?.(columnId);
        onClose?.();
    };

    const handleClear = () => {
        onClear?.(columnId);
        onClose?.();
    };

    return createPortal(
        <div
            style={{
                position: 'fixed',
                inset: `${topOffset}px 0 0 0`,
                zIndex: 1098,
                pointerEvents: 'none',
            }}
        >
            <div
                css={css`
                    height: 100%;
                    display: flex;
                    justify-content: flex-end;
                `}
            >
                <ClickAwayListener onClickAway={onClose}>
                    <Box
                        css={css`
                            pointer-events: auto;
                            width: ${width}px;
                            height: 100%;
                            background: white;
                            box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
                            border-left: 1px solid #e0e0e0;
                            display: flex;
                            flex-direction: column;
                        `}
                        className="p-3"
                    >
                        <Box
                            css={css`
                                flex: 1 1 auto;
                                overflow: auto;
                                display: flex;
                                flex-direction: column;
                                gap: 16px;
                            `}
                        >
                            {showClearButton && (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleClear}
                                    css={css`
                                        background: ${colors.greens.primary};
                                        color: white;
                                        &:hover {
                                            background: ${colors.greens[1] || colors.greens.primary};
                                        }
                                    `}
                                >
                                    Clear Filters
                                </Button>
                            )}

                            <Box>{children}</Box>
                        </Box>

                        {showApplyButton && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={2}
                                css={css`
                                    gap: 8px;
                                `}
                            >
                                <Button
                                    fullWidth
                                    color="primary"
                                    variant="contained"
                                    onClick={handleApply}
                                    css={css`
                                        background: ${colors.greens.primary};
                                        color: white;
                                        &:hover {
                                            background: ${colors.greens[1] || colors.greens.primary};
                                        }
                                    `}
                                >
                                    Apply
                                </Button>
                            </Box>
                        )}
                    </Box>
                </ClickAwayListener>
            </div>
        </div>,
        document.body
    );
};

export default ColumnFilterPanel;
